import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import AddModelModal from '../../../components/modals/AddModelModal';
import ImportModelModal from '../../../components/modals/ImportModelModal';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import {
  Brain, Play, Pause, Edit, Trash2, Plus,
  Search, Target, Clock,
  CheckCircle, RefreshCw, Download, Upload,
  Activity, Archive, History, GitBranch, Package,
  FileSpreadsheet, ChevronDown, X
} from 'lucide-react';
import toast from 'react-hot-toast';

const ModelManagement = () => {
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [editingModel, setEditingModel] = useState(null);
  const [modelToDelete, setModelToDelete] = useState(null);
  const [showAddModel, setShowAddModel] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`;
  });

  useEffect(() => {
    // Mock data for AI models with versions
    const mockModels = [
      {
        id: 1,
        name: "Ticket Classification",
        description: "Automatically classifies incoming tickets by category and priority",
        type: "Classification",
        status: "active",
        currentVersion: "v2.1.0",
        versions: [
          {
            version: "v2.1.0",
            status: "active",
            accuracy: 94.2,
            deploymentDate: "2025-01-10T10:00:00Z",
            performance: {
              precision: 94.2,
              recall: 92.8,
              f1Score: 93.5,
              inferenceTime: "45ms"
            },
            trainingData: "12,000 samples",
            lastTrained: "2025-01-10T09:30:00Z",
            createdBy: "AI Team",
            size: "2.3GB",
            framework: "TensorFlow 2.8"
          },
          {
            version: "v2.0.0",
            status: "archived",
            accuracy: 92.8,
            deploymentDate: "2025-01-05T10:00:00Z",
            performance: {
              precision: 92.8,
              recall: 91.5,
              f1Score: 92.1,
              inferenceTime: "52ms"
            },
            trainingData: "10,500 samples",
            lastTrained: "2025-01-05T09:00:00Z",
            createdBy: "AI Team",
            size: "2.1GB",
            framework: "TensorFlow 2.7"
          },
          {
            version: "v1.9.0",
            status: "archived",
            accuracy: 91.2,
            deploymentDate: "2024-12-28T10:00:00Z",
            performance: {
              precision: 91.2,
              recall: 90.8,
              f1Score: 91.0,
              inferenceTime: "58ms"
            },
            trainingData: "9,800 samples",
            lastTrained: "2024-12-28T08:30:00Z",
            createdBy: "AI Team",
            size: "1.9GB",
            framework: "TensorFlow 2.6"
          }
        ],
        totalPredictions: 125000,
        successRate: 94.2,
        lastUsed: "2025-01-15T14:30:00Z",
        createdAt: "2024-12-15T10:00:00Z"
      },
      {
        id: 2,
        name: "Sentiment Analysis",
        description: "Analyzes customer sentiment in tickets and chat messages",
        type: "Sentiment Analysis",
        status: "active",
        currentVersion: "v1.3.0",
        versions: [
          {
            version: "v1.3.0",
            status: "active",
            accuracy: 89.7,
            deploymentDate: "2025-01-15T16:45:00Z",
            performance: {
              precision: 89.7,
              recall: 87.3,
              f1Score: 88.5,
              inferenceTime: "32ms"
            },
            trainingData: "8,500 samples",
            lastTrained: "2025-01-15T16:00:00Z",
            createdBy: "AI Team",
            size: "1.8GB",
            framework: "PyTorch 1.12"
          },
          {
            version: "v1.2.0",
            status: "archived",
            accuracy: 87.9,
            deploymentDate: "2025-01-08T16:00:00Z",
            performance: {
              precision: 87.9,
              recall: 86.2,
              f1Score: 87.0,
              inferenceTime: "38ms"
            },
            trainingData: "7,200 samples",
            lastTrained: "2025-01-08T15:30:00Z",
            createdBy: "AI Team",
            size: "1.6GB",
            framework: "PyTorch 1.11"
          }
        ],
        totalPredictions: 210000,
        successRate: 89.7,
        lastUsed: "2025-01-15T14:25:00Z",
        createdAt: "2025-01-03T10:00:00Z"
      },
      {
        id: 3,
        name: "Auto-Response Generator",
        description: "Generates automated responses for common customer inquiries",
        type: "Text Generation",
        status: "training",
        currentVersion: "v1.0.0",
        versions: [
          {
            version: "v1.0.0",
            status: "training",
            accuracy: 91.5,
            deploymentDate: null,
            performance: {
              precision: 91.5,
              recall: 89.2,
              f1Score: 90.3,
              inferenceTime: "78ms"
            },
            trainingData: "15,000 samples",
            lastTrained: "2025-01-15T14:20:00Z",
            createdBy: "AI Team",
            size: "3.2GB",
            framework: "Transformers 4.21"
          }
        ],
        totalPredictions: 0,
        successRate: 0,
        lastUsed: null,
        createdAt: "2025-01-10T10:00:00Z"
      },
      {
        id: 4,
        name: "Ticket Assignment",
        description: "Automatically assigns tickets to appropriate agents based on skills and workload",
        type: "Assignment",
        status: "active",
        currentVersion: "v1.5.0",
        versions: [
          {
            version: "v1.5.0",
            status: "active",
            accuracy: 87.3,
            deploymentDate: "2025-01-12T14:00:00Z",
            performance: {
              precision: 87.3,
              recall: 85.1,
              f1Score: 86.2,
              inferenceTime: "56ms"
            },
            trainingData: "6,800 samples",
            lastTrained: "2025-01-12T13:30:00Z",
            createdBy: "AI Team",
            size: "1.4GB",
            framework: "Scikit-learn 1.1"
          },
          {
            version: "v1.4.0",
            status: "archived",
            accuracy: 85.9,
            deploymentDate: "2025-01-05T14:00:00Z",
            performance: {
              precision: 85.9,
              recall: 84.2,
              f1Score: 85.0,
              inferenceTime: "62ms"
            },
            trainingData: "6,200 samples",
            lastTrained: "2025-01-05T13:00:00Z",
            createdBy: "AI Team",
            size: "1.3GB",
            framework: "Scikit-learn 1.0"
          }
        ],
        totalPredictions: 145000,
        successRate: 87.3,
        lastUsed: "2025-01-15T14:15:00Z",
        createdAt: "2024-12-20T10:00:00Z"
      },
      {
        id: 5,
        name: "Fraud Detection",
        description: "Detects fraudulent tickets and suspicious activities",
        type: "Anomaly Detection",
        status: "inactive",
        currentVersion: "v0.9.0",
        versions: [
          {
            version: "v0.9.0",
            status: "inactive",
            accuracy: 82.1,
            deploymentDate: "2025-01-08T12:00:00Z",
            performance: {
              precision: 82.1,
              recall: 79.8,
              f1Score: 80.9,
              inferenceTime: "67ms"
            },
            trainingData: "4,500 samples",
            lastTrained: "2025-01-08T11:30:00Z",
            createdBy: "AI Team",
            size: "2.1GB",
            framework: "TensorFlow 2.8"
          }
        ],
        totalPredictions: 65000,
        successRate: 82.1,
        lastUsed: "2025-01-15T14:10:00Z",
        createdAt: "2025-01-05T10:00:00Z"
      }
    ];

    setModels(mockModels);
  }, []);

  const filteredModels = models.filter(model => {
    const matchesSearch = model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         model.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || model.status === statusFilter;
    const matchesType = typeFilter === 'all' || model.type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const handleVersionDeploy = (modelId, version) => {
    setModels(models.map(model => 
      model.id === modelId 
        ? { 
            ...model, 
            currentVersion: version,
            versions: model.versions.map(v => 
              v.version === version 
                ? { ...v, status: 'active' }
                : { ...v, status: 'archived' }
            )
          }
        : model
    ));
    toast.success(`Version ${version} deployed successfully`);
  };

  const handleModelAction = (modelId, action) => {
    setModels(models.map(model => 
      model.id === modelId 
        ? { ...model, status: action === 'activate' ? 'active' : 'inactive' }
        : model
    ));
    toast.success(`Model ${action === 'activate' ? 'activated' : 'deactivated'} successfully`);
  };

  const handleSaveModel = (modelData) => {
    if (editingModel) {
      setModels(models.map(m => m.id === editingModel.id ? { ...m, ...modelData } : m));
      toast.success('Model updated successfully');
      setEditingModel(null);
    } else {
      const newModel = {
        id: models.length + 1,
        ...modelData,
        versions: [{
          version: modelData.version || 'v1.0.0',
          status: 'active',
          accuracy: parseFloat(modelData.accuracy) || 0,
          deploymentDate: new Date().toISOString(),
          performance: {
            precision: parseFloat(modelData.precision) || 0,
            recall: parseFloat(modelData.recall) || 0,
            f1Score: parseFloat(modelData.f1Score) || 0,
            inferenceTime: modelData.inferenceTime || '0ms'
          },
          trainingData: modelData.trainingData || '0 samples',
          lastTrained: new Date().toISOString(),
          createdBy: 'AI Team',
          size: '0GB',
          framework: modelData.framework || 'TensorFlow'
        }],
        totalPredictions: 0,
        successRate: parseFloat(modelData.accuracy) || 0,
        lastUsed: null,
        createdAt: new Date().toISOString()
      };
      setModels([...models, newModel]);
      toast.success('Model added successfully');
    }
    setShowAddModel(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      setLastUpdated(`${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`);
      toast.success('Models refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh models');
    } finally {
      setRefreshing(false);
    }
  };

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      if (format === 'csv') {
        const csvContent = [
          'Name,Type,Status,Version,Success Rate,Total Predictions',
          ...models.map(model => 
            `${model.name},${model.type},${model.status},${model.currentVersion},${model.successRate}%,${model.totalPredictions}`
          )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `models-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
      toast.success(`Data exported as ${format.toUpperCase()} successfully`);
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteModel = (modelId) => {
    const model = models.find(m => m.id === modelId);
    if (model) {
      setModelToDelete(model);
      setShowDeleteConfirm(true);
    }
  };

  const confirmDeleteModel = () => {
    if (modelToDelete) {
      setModels(models.filter(m => m.id !== modelToDelete.id));
      toast.success(`Model "${modelToDelete.name}" deleted successfully`);
      setModelToDelete(null);
      setShowDeleteConfirm(false);
    }
  };

  const handleImport = async (importData) => {
    try {
      const { file, format, importMode } = importData;
      
      // Simulate file processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      if (format === 'csv') {
        // Parse CSV file
        const text = await file.text();
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim());
        
        const importedModels = lines.slice(1).map((line, index) => {
          const values = line.split(',').map(v => v.trim());
          const modelData = {};
          headers.forEach((header, i) => {
            modelData[header] = values[i] || '';
          });

          return {
            id: models.length + index + 1,
            name: modelData.name || `Imported Model ${index + 1}`,
            description: modelData.description || 'Imported model',
            type: modelData.type || 'Classification',
            status: modelData.status || 'inactive',
            currentVersion: modelData.version || 'v1.0.0',
            versions: [{
              version: modelData.version || 'v1.0.0',
              status: modelData.status || 'inactive',
              accuracy: parseFloat(modelData.accuracy) || 0,
              deploymentDate: null,
              performance: {
                precision: parseFloat(modelData.precision) || 0,
                recall: parseFloat(modelData.recall) || 0,
                f1Score: parseFloat(modelData.f1Score) || 0,
                inferenceTime: modelData.inferenceTime || '0ms'
              },
              trainingData: modelData.trainingData || '0 samples',
              lastTrained: new Date().toISOString(),
              createdBy: 'Imported',
              size: '0GB',
              framework: modelData.framework || 'TensorFlow'
            }],
            totalPredictions: 0,
            successRate: parseFloat(modelData.accuracy) || 0,
            lastUsed: null,
            createdAt: new Date().toISOString()
          };
        });

        if (importMode === 'create') {
          setModels([...models, ...importedModels]);
          toast.success(`${importedModels.length} new models imported successfully`);
        } else if (importMode === 'update') {
          // Update existing models by name
          setModels(models.map(model => {
            const imported = importedModels.find(im => im.name === model.name);
            return imported ? { ...model, ...imported } : model;
          }));
          toast.success('Models updated successfully');
        } else {
          // Upsert: create new or update existing
          const updatedModels = [...models];
          importedModels.forEach(imported => {
            const existingIndex = updatedModels.findIndex(m => m.name === imported.name);
            if (existingIndex >= 0) {
              updatedModels[existingIndex] = { ...updatedModels[existingIndex], ...imported };
            } else {
              updatedModels.push(imported);
            }
          });
          setModels(updatedModels);
          toast.success('Models imported/updated successfully');
        }
      } else if (format === 'json') {
        const text = await file.text();
        const jsonData = JSON.parse(text);
        const importedModels = Array.isArray(jsonData) ? jsonData : [jsonData];
        
        const processedModels = importedModels.map((modelData, index) => ({
          id: models.length + index + 1,
          ...modelData,
          versions: modelData.versions || [{
            version: modelData.version || 'v1.0.0',
            status: modelData.status || 'inactive',
            accuracy: parseFloat(modelData.accuracy) || 0,
            deploymentDate: null,
            performance: modelData.performance || {
              precision: 0,
              recall: 0,
              f1Score: 0,
              inferenceTime: '0ms'
            },
            trainingData: modelData.trainingData || '0 samples',
            lastTrained: new Date().toISOString(),
            createdBy: 'Imported',
            size: '0GB',
            framework: modelData.framework || 'TensorFlow'
          }],
          totalPredictions: modelData.totalPredictions || 0,
          successRate: parseFloat(modelData.accuracy) || 0,
          lastUsed: null,
          createdAt: new Date().toISOString()
        }));

        if (importMode === 'create') {
          setModels([...models, ...processedModels]);
        } else if (importMode === 'update') {
          setModels(models.map(model => {
            const imported = processedModels.find(im => im.name === model.name);
            return imported ? { ...model, ...imported } : model;
          }));
        } else {
          const updatedModels = [...models];
          processedModels.forEach(imported => {
            const existingIndex = updatedModels.findIndex(m => m.name === imported.name);
            if (existingIndex >= 0) {
              updatedModels[existingIndex] = { ...updatedModels[existingIndex], ...imported };
            } else {
              updatedModels.push(imported);
            }
          });
          setModels(updatedModels);
        }
        toast.success(`${processedModels.length} models imported successfully`);
      } else if (format === 'model') {
        // Handle model file upload (e.g., .h5, .pkl, .pt, .onnx)
        const newModel = {
          id: models.length + 1,
          name: file.name.replace(/\.[^/.]+$/, ''),
          description: `Imported model file: ${file.name}`,
          type: 'Imported',
          status: 'inactive',
          currentVersion: 'v1.0.0',
          versions: [{
            version: 'v1.0.0',
            status: 'inactive',
            accuracy: 0,
            deploymentDate: null,
            performance: {
              precision: 0,
              recall: 0,
              f1Score: 0,
              inferenceTime: '0ms'
            },
            trainingData: '0 samples',
            lastTrained: new Date().toISOString(),
            createdBy: 'Imported',
            size: `${(file.size / (1024 * 1024)).toFixed(2)}MB`,
            framework: file.name.endsWith('.h5') ? 'TensorFlow' : 
                      file.name.endsWith('.pkl') ? 'Scikit-learn' :
                      file.name.endsWith('.pt') ? 'PyTorch' :
                      file.name.endsWith('.onnx') ? 'ONNX' : 'Unknown'
          }],
          totalPredictions: 0,
          successRate: 0,
          lastUsed: null,
          createdAt: new Date().toISOString()
        };
        setModels([...models, newModel]);
        toast.success(`Model file "${file.name}" imported successfully`);
      }
    } catch (error) {
      throw new Error(error.message || 'Failed to import models');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      case 'training': return 'text-blue-600 bg-blue-100';
      case 'archived': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-4 w-4" />;
      case 'inactive': return <Pause className="h-4 w-4" />;
      case 'training': return <Clock className="h-4 w-4" />;
      case 'archived': return <Archive className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Breadcrumb and Last Updated */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-500">
          <span className="text-gray-700 font-medium">AI Orchestrator</span>
          <span className="mx-2">/</span>
          <span>Model Management</span>
        </div>
        <div className="text-sm text-gray-500">
          Last updated: {lastUpdated}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Model Management</h1>
          <p className="text-gray-600 mt-1 text-sm">Manage AI model lifecycle and versions</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <div className="relative">
            <Button 
              variant="outline"
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exporting}
              className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
            >
              <Download className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Exporting...' : 'Export'}
              <ChevronDown className="h-4 w-4" />
            </Button>
            
            {showExportMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowExportMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                  <div className="py-1">
                    <button
                      onClick={() => handleExport('csv')}
                      disabled={exporting}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileSpreadsheet className="h-4 w-4 text-green-600" />
                      Export as CSV
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          <Button 
            onClick={() => {
              setEditingModel(null);
              setShowAddModel(true);
            }} 
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Model
          </Button>
          <Button 
            variant="outline" 
            className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
            onClick={() => setShowImportModal(true)}
          >
            <Upload className="h-4 w-4" />
            Import
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-6">
        {/* Total Models Card */}
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Total Models</p>
                <p className="text-4xl font-extrabold text-gray-900 mb-2">{models.length}</p>
                <p className="text-xs font-medium text-gray-600">AI models registered</p>
              </div>
              <div className="ml-4 p-3 bg-violet-400 rounded-xl shadow-md">
                <Brain className="h-10 w-10 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Models Card */}
        <Card className="bg-gradient-to-br from-emerald-50 to-green-100 border-2 border-green-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-green-700 mb-2 uppercase tracking-wide">Active Models</p>
                <p className="text-4xl font-extrabold text-green-900 mb-2">
                  {models.filter(m => m.status === 'active').length}
                </p>
                <p className="text-xs font-medium text-green-600">Currently deployed</p>
              </div>
              <div className="ml-4 p-3 bg-green-500 rounded-xl shadow-md">
                <CheckCircle className="h-10 w-10 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Versions Card */}
        <Card className="bg-gradient-to-br from-pink-50 to-purple-100 border-2 border-pink-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-purple-700 mb-2 uppercase tracking-wide">Total Versions</p>
                <p className="text-4xl font-extrabold text-purple-900 mb-2">
                  {models.reduce((sum, m) => sum + m.versions.length, 0)}
                </p>
                <p className="text-xs font-medium text-purple-600">Version history</p>
              </div>
              <div className="ml-4 p-3 bg-purple-500 rounded-xl shadow-md">
                <GitBranch className="h-10 w-10 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Training Models Card */}
        <Card className="bg-gradient-to-br from-amber-50 to-orange-100 border-2 border-amber-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-orange-700 mb-2 uppercase tracking-wide">Training Models</p>
                <p className="text-4xl font-extrabold text-orange-900 mb-2">
                  {models.filter(m => m.status === 'training').length}
                </p>
                <p className="text-xs font-medium text-orange-600">In training phase</p>
              </div>
              <div className="ml-4 p-3 bg-orange-500 rounded-xl shadow-md">
                <Clock className="h-10 w-10 text-white animate-pulse" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Models Table */}
      <Card className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Package className="h-5 w-5 text-gray-700" />
              AI Models
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search models..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 border-gray-300">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-48 border-gray-300">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Classification">Classification</SelectItem>
                  <SelectItem value="Sentiment Analysis">Sentiment Analysis</SelectItem>
                  <SelectItem value="Text Generation">Text Generation</SelectItem>
                  <SelectItem value="Assignment">Assignment</SelectItem>
                  <SelectItem value="Anomaly Detection">Anomaly Detection</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            {filteredModels.map((model) => (
              <div 
                key={model.id} 
                className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-base text-gray-900">{model.name}</h3>
                      <Badge className={`${getStatusColor(model.status)} rounded-full px-2.5 py-0.5 text-xs font-medium`}>
                        <div className="flex items-center gap-1">
                          {getStatusIcon(model.status)}
                          <span className="capitalize">{model.status}</span>
                        </div>
                      </Badge>
                      <Badge variant="outline" className="rounded-full px-2.5 py-0.5 text-xs border-gray-300">
                        {model.type}
                      </Badge>
                      <Badge variant="outline" className="rounded-full px-2.5 py-0.5 text-xs border-gray-300">
                        {model.currentVersion}
                      </Badge>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">{model.description}</p>
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <GitBranch className="h-3.5 w-3.5" />
                        {model.versions.length} versions
                      </span>
                      <span className="flex items-center gap-1">
                        <Target className="h-3.5 w-3.5" />
                        {model.successRate}% success
                      </span>
                      <span className="flex items-center gap-1">
                        <Activity className="h-3.5 w-3.5" />
                        {model.totalPredictions.toLocaleString()} predictions
                      </span>
                      {model.lastUsed && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          Last used: {new Date(model.lastUsed).toLocaleDateString('en-GB', { 
                            day: '2-digit', 
                            month: '2-digit' 
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedModel(model)}
                      className="border-gray-300 hover:bg-gray-50"
                      title="View Version History"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleModelAction(model.id, model.status === 'active' ? 'deactivate' : 'activate')}
                      className="border-gray-300 hover:bg-gray-50"
                      title={model.status === 'active' ? 'Deactivate' : 'Activate'}
                    >
                      {model.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setEditingModel(model);
                        setShowAddModel(true);
                      }}
                      className="border-gray-300 hover:bg-gray-50"
                      title="Edit Model"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleDeleteModel(model.id)}
                      className="border-gray-300 hover:bg-red-50 hover:border-red-300"
                      title="Delete Model"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Add Model Modal */}
      <AddModelModal
        isOpen={showAddModel}
        onClose={() => {
          setShowAddModel(false);
          setEditingModel(null);
        }}
        model={editingModel}
        onSave={handleSaveModel}
      />

      {/* Import Model Modal */}
      <ImportModelModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImport}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setModelToDelete(null);
        }}
        onConfirm={confirmDeleteModel}
        title="Delete Model"
        message={
          modelToDelete 
            ? `Are you sure you want to delete "${modelToDelete.name}"? This action cannot be undone and all associated versions and data will be permanently removed.`
            : 'Are you sure you want to delete this model?'
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Version History Modal */}
      {selectedModel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Version History</h2>
                <p className="text-gray-600 text-sm mt-1">{selectedModel.name}</p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => setSelectedModel(null)}
                className="border-gray-300 hover:bg-gray-50"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              {selectedModel.versions.map((version, index) => (
                <div key={version.version} className="bg-gray-50 border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-lg text-gray-900">{version.version}</h3>
                      <Badge className={`${getStatusColor(version.status)} rounded-full px-2.5 py-0.5 text-xs font-medium`}>
                        <div className="flex items-center gap-1">
                          {getStatusIcon(version.status)}
                          <span className="capitalize">{version.status}</span>
                        </div>
                      </Badge>
                      <Badge variant="outline" className="rounded-full px-2.5 py-0.5 text-xs border-gray-300">
                        {version.accuracy}% accuracy
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      {version.status !== 'active' && (
                        <Button
                          size="sm"
                          onClick={() => handleVersionDeploy(selectedModel.id, version.version)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Deploy
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="border-gray-300 hover:bg-gray-50">
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 text-sm">
                    <div>
                      <span className="text-gray-600 text-xs">Framework:</span>
                      <p className="font-semibold text-gray-900 mt-1">{version.framework}</p>
                    </div>
                    <div>
                      <span className="text-gray-600 text-xs">Size:</span>
                      <p className="font-semibold text-gray-900 mt-1">{version.size}</p>
                    </div>
                    <div>
                      <span className="text-gray-600 text-xs">Training Data:</span>
                      <p className="font-semibold text-gray-900 mt-1">{version.trainingData}</p>
                    </div>
                    <div>
                      <span className="text-gray-600 text-xs">Created By:</span>
                      <p className="font-semibold text-gray-900 mt-1">{version.createdBy}</p>
                    </div>
                    <div>
                      <span className="text-gray-600 text-xs">Deployed:</span>
                      <p className="font-semibold text-gray-900 mt-1">
                        {version.deploymentDate ? new Date(version.deploymentDate).toLocaleDateString('en-GB') : 'Not deployed'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600 text-xs">Last Trained:</span>
                      <p className="font-semibold text-gray-900 mt-1">
                        {new Date(version.lastTrained).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="pt-3 border-t border-gray-200">
                    <h4 className="font-semibold text-sm text-gray-900 mb-3">Performance Metrics</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 text-xs">Precision:</span>
                        <p className="font-semibold text-gray-900 mt-1">{version.performance.precision}%</p>
                      </div>
                      <div>
                        <span className="text-gray-600 text-xs">Recall:</span>
                        <p className="font-semibold text-gray-900 mt-1">{version.performance.recall}%</p>
                      </div>
                      <div>
                        <span className="text-gray-600 text-xs">F1 Score:</span>
                        <p className="font-semibold text-gray-900 mt-1">{version.performance.f1Score}%</p>
                      </div>
                      <div>
                        <span className="text-gray-600 text-xs">Inference Time:</span>
                        <p className="font-semibold text-gray-900 mt-1">{version.performance.inferenceTime}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelManagement;
